import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

type TimerMode = "focus" | "shortBreak" | "longBreak";

interface TimerConfig {
  focus: number;
  shortBreak: number;
  longBreak: number;
}

const DEFAULT_CONFIG: TimerConfig = {
  focus: 25 * 60,
  shortBreak: 5 * 60,
  longBreak: 15 * 60,
};

interface PomodoroContextType {
  mode: TimerMode;
  timeLeft: number;
  isRunning: boolean;
  isComplete: boolean;
  sessionsCompleted: number;
  config: TimerConfig;
  toggleTimer: () => void;
  resetTimer: (newMode?: TimerMode) => void;
  setMode: (mode: TimerMode) => void;
  updateConfig: (newConfig: TimerConfig) => void;
}

const PomodoroContext = createContext<PomodoroContextType | undefined>(undefined);

export const usePomodoro = () => {
  const context = useContext(PomodoroContext);
  if (!context) {
    throw new Error("usePomodoro must be used within PomodoroProvider");
  }
  return context;
};

interface PomodoroProviderProps {
  children: ReactNode;
}

export const PomodoroProvider = ({ children }: PomodoroProviderProps) => {
  const [config, setConfig] = useState<TimerConfig>(() => {
    const saved = localStorage.getItem('pomodoro-config');
    return saved ? JSON.parse(saved) : DEFAULT_CONFIG;
  });
  const [mode, setModeState] = useState<TimerMode>("focus");
  const [timeLeft, setTimeLeft] = useState(config.focus);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [sessionsCompleted, setSessionsCompleted] = useState(0);

  const resetTimer = useCallback((newMode?: TimerMode) => {
    const targetMode = newMode || mode;
    setModeState(targetMode);
    setTimeLeft(config[targetMode]);
    setIsRunning(false);
    setIsComplete(false);
  }, [config, mode]);

  const toggleTimer = useCallback(() => {
    if (isComplete) {
      setIsComplete(false);
      setTimeLeft(config[mode]);
    }
    setIsRunning(prev => !prev);
  }, [isComplete, config, mode]);

  const setMode = useCallback((newMode: TimerMode) => {
    if (newMode !== mode) {
      setModeState(newMode);
      setTimeLeft(config[newMode]);
      setIsRunning(false);
      setIsComplete(false);
    }
  }, [config, mode]);

  const updateConfig = useCallback((newConfig: TimerConfig) => {
    setConfig(newConfig);
    localStorage.setItem('pomodoro-config', JSON.stringify(newConfig));
    setTimeLeft(newConfig[mode]);
    setIsRunning(false);
    setIsComplete(false);
  }, [mode]);

  // Timer countdown - runs globally even when sheet is closed
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsRunning(false);
          setIsComplete(true);
          
          // Play notification sound
          try {
            const audio = new Audio("data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU");
            audio.volume = 0.3;
            audio.play().catch(() => {});
          } catch {}

          // Auto-switch modes
          if (mode === "focus") {
            const newSessions = sessionsCompleted + 1;
            setSessionsCompleted(newSessions);
            setModeState(newSessions % 4 === 0 ? "longBreak" : "shortBreak");
          } else {
            setModeState("focus");
          }
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, mode, sessionsCompleted]);

  // Update timeLeft when mode changes after auto-switch
  useEffect(() => {
    if (isComplete) {
      setTimeLeft(config[mode]);
    }
  }, [mode, config, isComplete]);

  const value: PomodoroContextType = {
    mode,
    timeLeft,
    isRunning,
    isComplete,
    sessionsCompleted,
    config,
    toggleTimer,
    resetTimer,
    setMode,
    updateConfig,
  };

  return <PomodoroContext.Provider value={value}>{children}</PomodoroContext.Provider>;
};